import React, { useEffect, useRef, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import './modal.scss';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'wide';
  className?: string;
  /**
   * Extra class on the OVERLAY, not the modal box. Needed when the modal is
   * opened by a full-screen page, which paints above the overlay's default
   * z-index and would otherwise hide it completely.
   */
  overlayClassName?: string;
  // Enhanced modal features
  hasTabs?: boolean;
  hasFooter?: boolean;
  noPadding?: boolean;
  scrollable?: boolean;
  footerContent?: ReactNode;
  headerStatus?: ReactNode; // Status indicator shown to left of close button
}

/**
 * Open modals. Only used to count them: the last one out is what gives the page
 * its scrollbar back, so a prompt closing over a still-open parent must not.
 */
const openModals: object[] = [];

/**
 * Is this the frontmost modal?
 *
 * A modal opened from inside another one -- a confirm, or a small prompt raised
 * by a tab -- portals to <body>, so the parent's ref does not contain it and
 * the parent reads every click inside the child as a click outside itself,
 * closing itself the moment the child is touched. Only the frontmost modal
 * answers Escape and outside clicks.
 *
 * Frontmost is read from the DOM rather than from a mount-order stack: effects
 * run child-before-parent, so a stack pushed from an effect can come out
 * inverted when both open in the same commit. Portal containers are appended in
 * tree order, so the last .modal-overlay in the document is always the one on
 * top.
 */
const isFrontmostModal = (el: HTMLElement | null): boolean => {
  if (!el) return false;
  const overlays = document.querySelectorAll('.modal-overlay');
  const front = overlays[overlays.length - 1];
  return !front || front.contains(el);
};

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  className = '',
  overlayClassName = '',
  hasTabs = false,
  hasFooter = false,
  noPadding = false,
  scrollable = true,
  footerContent,
  headerStatus
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  // Identity for this modal's entry in the open-modal count above.
  const tokenRef = useRef<object>({});

  // Count this modal, and prevent body scrolling while any modal is open
  useEffect(() => {
    if (!isOpen) return;
    const token = tokenRef.current;
    openModals.push(token);
    document.body.style.overflow = 'hidden';

    return () => {
      const at = openModals.indexOf(token);
      if (at !== -1) openModals.splice(at, 1);
      // A modal closing over a still-open parent must not restore scrolling.
      if (openModals.length === 0) document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!isFrontmostModal(modalRef.current)) return;
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Handle escape key to close
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isFrontmostModal(modalRef.current)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const modalClasses = [
    'modal',
    size,
    className,
    hasTabs && 'has-tabs',
    hasFooter && 'has-footer',
    noPadding && 'no-padding',
    !scrollable && 'no-scroll'
  ].filter(Boolean).join(' ');

  const contentClasses = [
    'modal-content',
    noPadding && 'no-padding',
    !scrollable && 'no-scroll'
  ].filter(Boolean).join(' ');

  // Use portal to render modal at document body level
  // This ensures modal styles are not affected by parent container styles
  return createPortal(
    <div className={`modal-overlay ${overlayClassName}`.trim()}>
      <div 
        ref={modalRef}
        className={modalClasses}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'modal-title' : undefined}
      >
        {title && (
          <div className="modal-header">
            <h2 id="modal-title">{title}</h2>
            <div className="modal-header__right">
              {headerStatus && (
                <div className="modal-header__status">
                  {headerStatus}
                </div>
              )}
              <button 
                className="modal-close"
                onClick={onClose}
                aria-label="Close modal"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          </div>
        )}
        <div className={contentClasses}>
          {children}
        </div>
        {hasFooter && footerContent && (
          <div className="modal-footer">
            {footerContent}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default Modal; 